import { defaultModelFor } from '@shared/ai/models';
import { aiRequestInit, aiTarget, isServerRefusal } from '@/capture/AiGateway';
import { base64ToPngBlob } from '@/capture/EnhanceClient';

/**
 * Client for the image call that renders a ground swatch.
 *
 * Uses `/v1/images/generations` rather than the edits endpoint `EnhanceClient` talks to, because
 * there is no source image here — a swatch is made from a description, not from a render. Where the
 * request goes, and what credential it carries, is `AiGateway`'s call: the user's own key straight
 * to `api.openai.com` in the single-user build, or this origin's proxy on a hosted deployment.
 *
 * Square, and the smallest size the endpoint offers. That is still twice the 512 the normaliser
 * keeps, which is the right way round: downsampling 2:1 averages four samples per texel, so the
 * grain of the model's own rendering — which is noise, not material — is halved on the way in, and
 * the swatch that reaches the GPU is cleaner than a 512 render would have been. Asking for more than
 * 1024 would buy detail the mip chain discards at the angles ground is seen from, at the cost of
 * latency on a call the user is waiting on.
 */

export const SWATCH_MODEL = defaultModelFor('swatch');

/** See the note above: 1024 in, 512 kept, and the 2:1 downsample is doing real work. */
export const SWATCH_SIZE = '1024x1024';

export interface SwatchRequest {
  apiKey: string;
  prompt: string;
  model?: string;
  signal?: AbortSignal;
  /** Injected for tests. Defaults to the global `fetch`. */
  fetchImpl?: typeof fetch;
}

export class SwatchRenderError extends Error {
  constructor(
    message: string,
    readonly status: number | null,
    readonly retryable: boolean,
  ) {
    super(message);
    this.name = 'SwatchRenderError';
  }
}

/**
 * The constraints every swatch render gets, whatever the recipe's own prompt says.
 *
 * Appended rather than prepended: the recipe's prompt is the *subject* and these are the *framing*,
 * and an image model weights the opening of a prompt most heavily. The three that matter most are
 * flat lighting (a baked shadow fights the sun in the scene), no scale objects (a leaf in the
 * texture is repeated across the whole driveway), and no border (a framed swatch tiles as a grid of
 * picture frames, which is the single most obvious way this can fail).
 */
const SWATCH_CONSTRAINTS = `

Strict requirements: perfectly flat top-down orthographic view; the frame is filled edge to edge
with the material and nothing else; soft even overcast lighting with no cast shadows and no
directional highlight; no border, frame, margin, vignette, label, watermark or text; no plants,
tools, hands, edging, or any object that establishes scale; uniform focus across the whole frame
with no depth-of-field blur; seamless and tileable.`;

export async function renderSwatch(request: SwatchRequest): Promise<Blob> {
  const doFetch = request.fetchImpl ?? globalThis.fetch;
  if (typeof doFetch !== 'function') {
    throw new SwatchRenderError('No fetch implementation is available in this environment.', null, false);
  }

  const target = aiTarget('swatch', request.apiKey);
  let response: Response;
  try {
    response = await doFetch(target.url, {
      method: 'POST',
      ...aiRequestInit(target, { 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        model: request.model ?? SWATCH_MODEL,
        prompt: `${request.prompt}${SWATCH_CONSTRAINTS}`,
        size: SWATCH_SIZE,
        n: 1,
        output_format: 'png',
      }),
      ...(request.signal ? { signal: request.signal } : {}),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new SwatchRenderError('Could not reach the OpenAI API. Check your network connection.', null, true);
  }

  if (!response.ok) throw await describeFailure(response);

  const payload = (await response.json()) as { data?: { b64_json?: string }[] };
  const encoded = payload.data?.[0]?.b64_json;
  if (typeof encoded !== 'string' || encoded.length === 0) {
    throw new SwatchRenderError('The API returned no swatch image.', response.status, true);
  }
  return base64ToPngBlob(encoded);
}

async function describeFailure(response: Response): Promise<SwatchRenderError> {
  const { detail, code } = await readErrorDetail(response);

  // A refusal this app's own server already wrote a sentence for — out of credits, over the cost
  // cap, suspended, AI switched off. The branches below would replace it with advice about API-key
  // permissions or billing at platform.openai.com, neither of which exists for a hosted user.
  if (isServerRefusal(code) && detail !== null) {
    return new SwatchRenderError(detail, response.status, false);
  }

  switch (response.status) {
    case 401:
      return new SwatchRenderError(
        'OpenAI rejected the API key. Check that it is correct and still active.',
        401,
        false,
      );
    case 403:
      return new SwatchRenderError(
        "This API key is not permitted to generate images. Verify the key's project has image models enabled.",
        403,
        false,
      );
    case 429:
      return new SwatchRenderError(
        'Rate limited or out of quota. Wait a moment and retry, or check your billing at platform.openai.com.',
        429,
        true,
      );
    default:
      break;
  }
  if (response.status >= 500) {
    return new SwatchRenderError('OpenAI had a server error. Retrying usually works.', response.status, true);
  }
  return new SwatchRenderError(
    detail ?? `The swatch render failed (HTTP ${response.status}).`,
    response.status,
    false,
  );
}

/** The message and the machine-readable reason. Both upstreams answer `{ error: { message, code } }`. */
async function readErrorDetail(response: Response): Promise<{
  detail: string | null;
  code: string | null;
}> {
  try {
    const body = (await response.json()) as { error?: { message?: string; code?: string } };
    const message = body.error?.message;
    const code = body.error?.code;
    return {
      detail: typeof message === 'string' && message.length > 0 ? message : null,
      code: typeof code === 'string' && code.length > 0 ? code : null,
    };
  } catch {
    return { detail: null, code: null };
  }
}
